/**
 * IMPORTS
 */
import { useMemo } from "react";

import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { INTERVALO_ATUALIZACAO_MS } from "@/hooks/conversa-cliente/use-notificacoes";

import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import {
  handleListarMinhasMensagens,
  handleResponderMinhasMensagens,
} from "@/domain/use-cases/conversa-cliente";
import type { IResponderConversaPayload } from "@/interface/conversa-cliente";

const CONVERSA_QUERY_KEY = "conversa-cliente";
const ITENS_POR_PAGINA = 20;

/**
 * Conversa da empresa do cliente logado. A API devolve da mais recente para a mais antiga;
 * aqui as páginas são invertidas para exibir como chat (mais antigas em cima).
 * `habilitado` = falso enquanto o usuário não participa (a API responderia 403).
 */
function useMinhasMensagens(busca?: string, habilitado = true) {
  const accessToken = useStoreZustandUserAuth((state) => state.token);
  const queryClient = useQueryClient();

  const timeline = useInfiniteQuery({
    queryKey: [CONVERSA_QUERY_KEY, busca ?? ""],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      handleListarMinhasMensagens(accessToken!, pageParam, ITENS_POR_PAGINA, busca),
    getNextPageParam: (ultima) =>
      ultima.pagination.current_page < ultima.pagination.last_page
        ? ultima.pagination.current_page + 1
        : undefined,
    enabled: Boolean(accessToken && habilitado),
    refetchInterval: habilitado ? INTERVALO_ATUALIZACAO_MS : false,
  });

  const mensagens = useMemo(
    () => (timeline.data?.pages.flatMap((pagina) => pagina.data) ?? []).slice().reverse(),
    [timeline.data]
  );

  const responder = useMutation({
    mutationFn: (payload: IResponderConversaPayload) =>
      handleResponderMinhasMensagens(accessToken!, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CONVERSA_QUERY_KEY] }),
  });

  return {
    mensagens,
    totalMensagens: timeline.data?.pages[0]?.pagination.total ?? 0,
    carregando: timeline.isLoading,
    erro: timeline.isError,
    recarregar: timeline.refetch,
    temAnteriores: timeline.hasNextPage,
    carregarAnteriores: timeline.fetchNextPage,
    carregandoAnteriores: timeline.isFetchingNextPage,
    responder,
  };
}

/**
 * EXPORTS
 */
export { CONVERSA_QUERY_KEY, useMinhasMensagens };
